/* eslint-disable no-use-before-define */
/* eslint-disable radix */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import classnames from 'classnames';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

import { Menu } from 'uikit/menus';
import { Button } from 'uikit/buttons';
import { getMenuItems, Video } from '@/exceed';

import styles from '../styles/videoPlayer.module.scss';

import useVideoCaptions from '../hooks/useVideoCaptions';
import useVideoPlayer from '../hooks/useVideoPlayer';

import getEmbedYouTubeUrl from '../helpers/getEmbedYouTubeUrl';
import getYouTubeUrlRegex from '../helpers/getYouTubeUrlRegex';
import getVideoProviderByUrl from '../helpers/getVideoProviderByUrl';

import VideoPlayerNextUp from './videoPlayerNextUp';

export default (props) => {
  const {
    handleTogglePlaying,
    handleSeekChange,
    handleSetPlaybackRate,
    handleDuration,
    handleProgress,
    handleFullScreen,
    handleTogglePIP,
    handleSeekBarChange,
    handleVolumeChange,
    handleToggleCaptions,
    handleBookmark,
    handleNextupVideoPopup,
    muted,
    pip,
    playing,
    volume,
    played,
    duration,
    playbackRate,
    fullscreen,
    captionsMode,
    ref,
    isNextupVideoShowing,
  } = useVideoPlayer({
    createEnrollment: props.createEnrollment,
    updateEnrollment: props.updateEnrollment,
    enrollmentData: props.enrollmentData,
    video: props.video,
    id: props.id,
    shouldNotAutoplay: props.shouldNotAutoplay,
    shouldNotEnroll: props.shouldNotEnroll,
    selectedAgendaItem: props.selectedAgendaItem,
  });

  const isInLiveMode = useParams().type === 'live';

  const [isReady, setIsReady] = useState(false);

  const [hasStarted, setHasStarted] = useState(false);

  const [isFacebookVideo, setIsFacebookVideo] = useState(false);

  // Need to override the prop for Facebook videos to prevent loading in floating state.
  // It is reset in onReady.
  const [shouldNotFloat, setShouldNotFloat] = useState(true);

  const isYouTubeLink = getYouTubeUrlRegex().test(props.video);

  const replacementKey = isInLiveMode ? 'replace-trailer' : 'replace-video';

  const hasCaptions = useVideoCaptions({
    video: props.video,
    caption: props.caption,
    ref,
    hasStarted,
  });

  const onReady = (context) => {
    if (!isReady) {
      const videoIsFacebook = /^facebook-player/.test(context.player?.player?.playerID);
      setIsFacebookVideo(videoIsFacebook);
      setShouldNotFloat(props.shouldNotFloat || videoIsFacebook);
      const params = new URL(document.location).searchParams;
      const timeStamp = parseInt(params.get('t'));
      const resumeTimeFromEnrollment = props.enrollmentData?.is_complete
        ? 0
        : props.enrollmentData?.time;
      const resumeTime = timeStamp || resumeTimeFromEnrollment;

      if (resumeTime) ref.current.seekTo(resumeTime, 'seconds');
      setIsReady(true);
    }
  };

  const onStart = async () => {
    const player = ref.current?.getInternalPlayer;
    // Adds in a specific case for when the video provider will handle its own default caption behaviour.
    if (player().unloadModule) player().unloadModule('captions');

    if (player().disableTextTrack) {
      const textTracks = await player().getTextTracks();

      if (textTracks[0]) await player().disableTextTrack(textTracks[0].language);
    }

    setHasStarted(true);
  };

  const defaultConfigs = {
    attributes: {
      controlsList: 'nodownload',
    },
  };

  const captionsUrl = props.caption?.url;

  const provider = getVideoProviderByUrl({ videoUrl: props.video });

  const captionsConfig =
    captionsUrl && provider === 'file'
      ? {
          tracks: [
            {
              kind: 'captions',
              src: captionsUrl,
              srcLang: 'en',
              default: true,
            },
          ],
          attributes: {
            ...defaultConfigs.attributes,
            crossorigin: 'anonymous',
          },
          mimeTypes: {
            captions: 'text/vtt',
          },
        }
      : defaultConfigs;

  return (
    <div
      className={classnames(styles.container, props.className, {
        [styles.isFacebookVideo]: isFacebookVideo,
      })}
      id={1}
    >
      <div className={classnames(styles.reactPlayerContainer)}>
        {isNextupVideoShowing && props.upNextVideo && (
          <VideoPlayerNextUp
            item={props.upNextVideo}
            handleRemovePopup={() => handleNextupVideoPopup(false)}
          />
        )}
        {!props.shouldHideMenu && (
          <Menu
            className={styles.menu}
            horizontalPosition="right"
            shouldAnimate={false}
            component={<Button size="small" icon={<MoreHoriz />} color="secondary" />}
          >
            <ul>
              {getMenuItems([
                {
                  key: replacementKey,
                  onClick: () => props.onReplaceVideoClick(replacementKey),
                  icon: '',
                  isDisabled: props.isReplaceVideoDisabled,
                },
              ])}
            </ul>
          </Menu>
        )}
        <Video
          onReady={onReady}
          onStart={onStart}
          innerRef={ref}
          width="100%"
          height="100%"
          url={isYouTubeLink ? getEmbedYouTubeUrl({ url: props.video }) : props.video}
          pip={pip}
          canPlayPip={!isFacebookVideo}
          playing={playing}
          controls={props.shouldHideCustomControls}
          loop={false}
          playbackRate={playbackRate}
          progressInterval={100}
          volume={volume}
          muted={muted}
          onPlay={() => handleTogglePlaying({ shouldPlay: true })}
          onPause={() => handleTogglePlaying({ shouldPlay: false })}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onEnded={isInLiveMode ? props.onEnded : () => handleNextupVideoPopup(true)}
          config={{ file: captionsConfig }}
          onContextMenu={(event) => {
            event.preventDefault();
          }}
          shouldHideCustomControls={props.shouldHideCustomControls}
          handleTogglePlaying={handleTogglePlaying}
          handleSeekChange={handleSeekChange}
          handleSetPlaybackRate={handleSetPlaybackRate}
          handleFullScreen={handleFullScreen}
          handleTogglePIP={handleTogglePIP}
          handleSeekBarChange={handleSeekBarChange}
          handleVolumeChange={handleVolumeChange}
          handleToggleCaptions={handleToggleCaptions}
          handleBookmark={handleBookmark}
          played={played}
          duration={duration}
          fullscreen={fullscreen}
          hasBookmark={props.hasBookmark}
          hasCaptions={hasCaptions}
          captionsMode={captionsMode}
          screenSize={props.screenSize}
          shouldNotFloat={shouldNotFloat}
        />
      </div>
    </div>
  );
};
